/*
 * The Settings frame (2026-10-29, settings parity): a grouped side menu with one search box over every setting, and an
 * Overview that shows each big switch On or Off with a button to where it changes. Settings that belong to one feature
 * live with it (Feature.tsx, owner 2026-10-29); this menu and search still list them and open them at their card.
 */
import { Link, Outlet, useRouter, useRouterState } from "@tanstack/react-router";
import {
  AUTOMATIONS,
  AUTOMATION_KINDS,
  formatMoney,
  routesOn,
  STEWARD_VERSION,
  type Action,
  type Cents,
  type Settings,
} from "@steward/shared";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useAutomations, useSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Icon, type IconName } from "../../components/Icon";
import { Banner, Card, Page, StatusLabel, type Status } from "../../components/parts";

type Group = "Your business" | "Money" | "Work" | "Team" | "Messages" | "Data and safety";
const GROUPS: Group[] = ["Your business", "Money", "Work", "Team", "Messages", "Data and safety"];

interface Ctx {
  can: (a: Action) => boolean;
  level: string;
  trade: string;
}

/** One page of settings, with the settings on it that search can find: [what it is called, card anchor, other words]. */
interface SettingsPage {
  to: string;
  label: string;
  blurb: string;
  group: Group;
  icon: IconName;
  action: Action;
  when?: (c: Ctx) => boolean;
  /** Lives with its feature (its own page, with a settings button on the feature). */
  /** Lives in its own section (Invoices, Schedule, HR...), which links to it. Search finds it; the menu does not list it. */
  outside?: boolean;
  items: [label: string, anchor?: string | undefined, words?: string][];
}

/** Head office has no clients, work or invoices, so it skips the pages for those (owner, 2026-11-02). */
const notHq = (c: Ctx): boolean => c.level !== "hq";

const PAGES: SettingsPage[] = [
  {
    to: "/settings/business",
    label: "Business info",
    blurb: "Name, phone, address, time zone, brand color, logo, links",
    group: "Your business",
    icon: "building",
    action: "company.read",
    items: [
      ["Business name", "info"],
      ["Phone and email", "info", "contact"],
      ["Address", "info"],
      ["Time zone", "info", "clock hours"],
      ["Brand color", "brand", "colour theme"],
      ["Web address", "brand", "domain link url app"],
      ["Logo", "brand", "image"],
      ["Links on client pages", "links", "facebook instagram google yelp nextdoor angi bbb social networks footer"],
    ],
  },
  {
    to: "/settings/plan",
    label: "Plan and billing",
    blurb: "Your monthly fee and paid add-ons",
    group: "Your business",
    icon: "money",
    action: "franchise.read",
    when: (c) => c.level === "location",
    items: [
      ["Monthly fee", undefined, "franchise royalty bill"],
      ["Paid add-ons", undefined, "eunice cold email business finder"],
    ],
  },
  {
    to: "/invoices/settings",
    label: "Invoices and payments",
    blurb: "Invoice words, tips, ways to pay, auto charge, past due",
    group: "Money",
    icon: "invoices",
    action: "settings.read",
    outside: true,
    when: notHq,
    items: [
      ["Invoice email subject and message", "invoices", "template words text"],
      ["Tips on the pay page", "invoices", "tip percent gratuity"],
      ["Ways to pay", "invoices", "card apple google pay bank ach"],
      ["Klarna", "invoices", "pay in 4 financing least minimum"],
      ["Saved cards charged by themselves", "invoices", "auto charge autobill hold"],
      ["Past due", "invoices", "due days terms late overdue homes businesses"],
      ["Visit photos on the pay page", "invoices", "proof pictures"],
      ["Cash or check note", "on-invoice", "payment instructions"],
      ["Small print", "on-invoice", "disclaimer contract terms footer"],
      ["Bill part of a job early", "on-invoice", "progress payments deposit"],
    ],
  },
  {
    to: "/quotes/settings",
    label: "Price list",
    blurb: "What the auto quoter charges",
    group: "Money",
    icon: "tag",
    action: "settings.read",
    outside: true,
    when: notHq,
    items: [
      ["Services", undefined, "per room square feet minimum price"],
      ["Repeat visits", undefined, "weekly biweekly monthly discount"],
      ["Add-ons", undefined, "extras"],
      ["Rooms every home has", undefined, "base rooms"],
    ],
  },
  {
    to: "/proposals/settings",
    label: "Proposals",
    blurb: "Commercial bid pricing and the letter",
    group: "Money",
    icon: "file",
    action: "settings.read",
    outside: true,
    when: (c) => c.trade === "cleaning",
    items: [
      ["Commercial pricing", undefined, "minimum visit margin supplies buffer bids"],
      ["Crew pay an hour", undefined, "wage tiers"],
      ["Cleaning speeds", undefined, "square feet an hour restroom fixtures construction"],
      ["Proposal letter", undefined, "intro sign highlights reviews insurance terms quality"],
    ],
  },
  {
    to: "/schedule/settings",
    label: "Schedule and field",
    blurb: "Routes, capacity, arrival window, colors",
    group: "Work",
    icon: "schedule",
    action: "settings.read",
    outside: true,
    when: notHq,
    items: [
      ["Use routes", "routes", "route view best order"],
      ["Where the teams start", "routes", "start address"],
      ["Close full days", "capacity", "capacity teams per day team size hours"],
      ["Arrival window", "arrival", "estimated arrival time before after"],
      ["Colors by word", "colors", "calendar colors"],
      ["On my way guess", "field", "minutes drive"],
    ],
  },
  {
    to: "/requests/settings",
    label: "Website and requests",
    blurb: "Sites that send requests, ZIP codes you serve",
    group: "Work",
    icon: "requests",
    action: "settings.read",
    outside: true,
    when: notHq,
    items: [
      ["Websites that send requests", "websites", "quote form origin"],
      ["ZIP codes we serve", "zips", "territories area"],
      ["Newsletter and popup signups", "signups", "email list"],
    ],
  },
  {
    to: "/hr/settings",
    label: "Crew and payroll",
    blurb: "Pay period, payday, pool, crew money, clock out",
    group: "Team",
    icon: "team",
    action: "settings.read",
    outside: true,
    items: [
      ["Pay period", "payroll", "weekly two weeks biweekly"],
      ["Payday", "payroll"],
      ["Default pool", "payroll", "percent split"],
      ["Crew take cash or check", "crew", "collect money"],
      ["Clock out on their own", "crew", "auto clock out timer"],
      ["Each person's settings", "people", "see job prices my pay language spanish role"],
    ],
  },
  {
    to: "/settings/automations",
    label: "Automatic messages",
    blurb: "Reminders, follow-ups, receipts, reviews",
    group: "Messages",
    icon: "repeat",
    action: "settings.read",
    when: notHq,
    items: [],
  },
  {
    to: "/settings/alerts",
    label: "Alerts",
    blurb: "What the bell tells the office",
    group: "Messages",
    icon: "bell",
    action: "settings.read",
    when: notHq,
    items: [
      ["New request alert", "alerts", "notification lead"],
      ["Quote approved alert", "alerts", "notification"],
      ["Payment alert", "alerts", "notification paid"],
    ],
  },
  {
    to: "/marketing/settings",
    label: "Reviews and referrals",
    blurb: "Google review link, referral reward, posting",
    group: "Messages",
    icon: "marketing",
    action: "settings.read",
    outside: true,
    when: notHq,
    items: [
      ["Google review link", "reviews", "review url"],
      ["Referral reward", "reviews", "refer a friend credit"],
      ["Posting to Google and Facebook", "posting", "social posts"],
      ["Replies to reviews", "posting", "auto reply ai"],
    ],
  },
  {
    to: "/receptionist",
    label: "Eunice",
    blurb: "The text receptionist",
    group: "Messages",
    icon: "message",
    action: "settings.read",
    outside: true,
    when: notHq,
    items: [
      ["Eunice mode", "eunice", "receptionist ai off practice live"],
      ["His name", "eunice", "receptionist"],
      ["What he knows", "eunice", "brief prices careers quote page"],
      ["Text back missed calls", "eunice", "missed call"],
      ["Owner phones", "eunice"],
      ["Lessons", undefined, "when a customer says answer like this"],
    ],
  },
  {
    to: "/settings/messages",
    label: "Texts and emails",
    blurb: "Test mode, quiet hours, message log",
    group: "Messages",
    icon: "mail",
    action: "outbox.read",
    items: [
      ["Real texts, emails and card charges", "sending", "live test mode"],
      ["Quiet hours", "sending", "8 pm 8 am"],
      ["Message log", undefined, "outbox sent failed"],
    ],
  },
  {
    to: "/settings/keys",
    label: "Connected services",
    blurb: "Google, Meta, Quo, Resend, Claude, Plaid, Stripe",
    group: "Data and safety",
    icon: "key",
    action: "keys.read",
    items: [["Keys", "keys", "stripe quo resend google meta facebook anthropic claude plaid api webhook"]],
  },
  {
    to: "/settings/data",
    label: "Import and export",
    blurb: "Bring clients in, download everything",
    group: "Data and safety",
    icon: "file",
    action: "client.bulk",
    when: notHq,
    items: [
      ["Import clients", undefined, "jobber bookingkoala csv"],
      ["Download a spreadsheet", undefined, "export csv zip backup"],
    ],
  },
  {
    to: "/settings/deleted",
    label: "Recently deleted",
    blurb: "Bring back what was deleted",
    group: "Data and safety",
    icon: "trash",
    action: "client.update",
    items: [["Restore", undefined, "undo bring back trash"]],
  },
  {
    to: "/settings/claude",
    label: "Claude",
    blurb: "Use Claude as your helper",
    group: "Data and safety",
    icon: "sparkle",
    // Owners and managers of a location (Claude connects to locations only; dispatch has no insights.read).
    action: "insights.read",
    when: (c) => c.level === "location",
    items: [["Connect Claude", undefined, "ai assistant mcp connector anthropic chat helper"]],
  },
  {
    to: "/settings/security",
    label: "Security and activity",
    blurb: "Two-step sign in, devices, who changed what",
    group: "Data and safety",
    icon: "shield",
    action: "myday.read",
    items: [
      ["Two-step sign in", "two-step", "2fa authenticator backup codes password"],
      ["Signed in devices", undefined, "sessions sign out everywhere"],
      ["Activity", undefined, "audit log history"],
    ],
  },
];

/** The pages this person may open here. */
function usePages(): SettingsPage[] {
  const { can, company } = useSession();
  const ctx: Ctx = { can, level: company.level, trade: company.trade };
  return PAGES.filter((p) => (p.to === "/settings/security" || can(p.action)) && (!p.when || p.when(ctx)));
}

interface Hit {
  label: string;
  page: string;
  path: string;
}

/** Every setting this person can open, for search: each page's items plus each automatic message. */
function useIndex(pages: SettingsPage[]): (Hit & { hay: string })[] {
  return useMemo(() => {
    const out: (Hit & { hay: string })[] = [];
    for (const p of pages) {
      out.push({ label: p.label, page: p.group, path: p.to, hay: `${p.label} ${p.blurb}`.toLowerCase() });
      for (const [label, anchor, words] of p.items)
        out.push({
          label,
          page: p.label,
          path: anchor ? `${p.to}#${anchor}` : p.to,
          hay: `${label} ${words ?? ""} ${p.label}`.toLowerCase(),
        });
      if (p.to === "/settings/automations")
        for (const k of AUTOMATION_KINDS)
          out.push({
            label: AUTOMATIONS[k].title,
            page: p.label,
            path: `/settings/automations/${k}`,
            hay: `${AUTOMATIONS[k].title} ${k.replace(/_/g, " ")} automatic message text email`.toLowerCase(),
          });
    }
    return out;
  }, [pages]);
}

function SettingsSearch({ pages }: { pages: SettingsPage[] }) {
  const index = useIndex(pages);
  const router = useRouter();
  const [q, setQ] = useState("");
  const [at, setAt] = useState(0);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const hits = words.length ? index.filter((h) => words.every((w) => h.hay.includes(w))).slice(0, 12) : [];
  const go = (h: Hit) => {
    setQ("");
    router.history.push(h.path);
  };
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAt(Math.min(at + 1, hits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAt(Math.max(at - 1, 0));
    } else if (e.key === "Enter" && hits[at]) {
      go(hits[at]);
    } else if (e.key === "Escape") {
      setQ("");
    }
  };
  return (
    <div className="settings-search">
      <div className="input">
        <span className="affix">
          <Icon name="search" size={16} />
        </span>
        <input
          type="search"
          placeholder="Search settings"
          aria-label="Search settings"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setAt(0);
          }}
          onKeyDown={keys}
        />
      </div>
      {words.length ? (
        <ul className="settings-hits" role="listbox" aria-label="Settings found">
          {hits.length ? (
            hits.map((h, i) => (
              <li key={`${h.path}-${h.label}`} role="option" aria-selected={i === at}>
                <button type="button" onClick={() => go(h)} onMouseEnter={() => setAt(i)}>
                  <b>{h.label}</b>
                  <span className="muted small">{h.page}</span>
                </button>
              </li>
            ))
          ) : (
            <li className="muted small settings-none">Nothing matches. Try a shorter word.</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

/** Opening a page at #card scrolls to that card and lights it up for a moment, once the page has loaded. */
export function useScrollToCard() {
  const { pathname, hash } = useRouterState({ select: (s) => ({ pathname: s.location.pathname, hash: s.location.hash }) });
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!hash) return;
    let tries = 0;
    let frame = 0;
    const look = () => {
      const el = document.getElementById(hash);
      if (!el) {
        if (tries++ < 60) frame = requestAnimationFrame(look);
        return;
      }
      const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ block: "start", behavior: still ? "auto" : "smooth" });
      el.classList.add("flash");
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => el.classList.remove("flash"), 1600);
    };
    frame = requestAnimationFrame(look);
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);
}

export function SettingsLayout() {
  const { role } = useSession();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const pages = usePages();
  useScrollToCard();
  if (role === "crew") return <Outlet />;
  const here = (to: string) => path === to || path.startsWith(`${to}/`);
  return (
    <Page title="Settings">
      <div className="settings-layout">
        <aside className="settings-side">
          <SettingsSearch pages={pages} />
          <nav className="subnav" aria-label="Settings">
            <Link
              to="/settings"
              activeOptions={{ exact: true }}
              data-status={path === "/settings" || path === "/settings/" ? "active" : undefined}
            >
              <Icon name="sliders" size={16} />
              Overview
            </Link>
            {GROUPS.map((g) => {
              const inGroup = pages.filter((p) => p.group === g && !p.outside);
              if (!inGroup.length) return null;
              return (
                <div key={g} className="subnav-group">
                  <span className="subnav-title">{g}</span>
                  {inGroup.map((p) => (
                    <Link key={p.to} to={p.to as "/settings"} data-status={here(p.to) ? "active" : undefined}>
                      <Icon name={p.icon} size={16} />
                      {p.label}
                    </Link>
                  ))}
                </div>
              );
            })}
          </nav>
        </aside>
        <div className="stack settings-main">
          <Outlet />
        </div>
      </div>
    </Page>
  );
}

// Overview ---------------------------------------------------------------------------------------------------------

interface Glance {
  title: string;
  what: string;
  status: [Status, string];
  to: string;
}

const onOff = (on: boolean): [Status, string] => (on ? ["success", "On"] : ["inactive", "Off"]);

function glances(s: Settings, trade: string, autoOn: number | null, autoReady: number): Glance[] {
  const lawn = trade === "lawn";
  const alertsOn = [s.alerts.newRequest, s.alerts.quoteApproved, s.alerts.paid].filter(Boolean).length;
  const arrive =
    s.schedule.arriveBefore || s.schedule.arriveAfter
      ? `${String(s.schedule.arriveBefore)} min before, ${String(s.schedule.arriveAfter)} after`
      : "Start time only";
  const rows: (Glance | false)[] = [
    {
      title: "Real texts, emails and card charges",
      what: "Off means test mode. Nothing goes to clients.",
      status: s.messaging.live ? ["success", "On"] : ["warning", "Test mode"],
      to: "/settings/messages#sending",
    },
    autoOn !== null && {
      title: "Automatic messages",
      what: "Reminders, follow-ups, receipts and review asks",
      status: [autoOn ? "success" : "inactive", `${String(autoOn)} of ${String(autoReady)} on`],
      to: "/settings/automations",
    },
    {
      title: "Tips on the pay page",
      what: s.invoices.tipPercents.map((p) => `${String(p)}%`).join(", "),
      status: onOff(s.invoices.tips),
      to: "/invoices/settings#invoices",
    },
    {
      title: "Bank payment",
      what: "Clients pay from their bank",
      status: onOff(s.invoices.payWays.bank),
      to: "/invoices/settings#invoices",
    },
    {
      title: "Klarna",
      what: `Pay in 4 on one-time work over ${formatMoney(s.invoices.klarnaMinCents as Cents)}`,
      status: onOff(s.invoices.payWays.klarna),
      to: "/invoices/settings#invoices",
    },
    {
      title: "Bill part of a job early",
      what: "Progress invoices on one-time work",
      status: onOff(s.invoices.progress),
      to: "/invoices/settings#on-invoice",
    },
    {
      title: "Routes",
      what: "Route view, Best order, stops in order",
      status: onOff(routesOn(s, trade)),
      to: "/schedule/settings#routes",
    },
    (!lawn || s.booking.fromQuote) && {
      title: "Close full days",
      what: "Count teams and stop booking a full day",
      status: onOff(s.capacity.on),
      to: "/schedule/settings#capacity",
    },
    {
      title: "Arrival window",
      what: "What clients hear in booking and reminder texts",
      status: ["informative", arrive],
      to: "/schedule/settings#arrival",
    },
    {
      title: "Crew take cash or check",
      what: "Crew say if the client paid them",
      status: onOff(s.crew.collectsMoney),
      to: "/hr/settings#crew",
    },
    {
      title: "Clock out on their own",
      what: "20 quiet minutes after the last visit",
      status: onOff(s.field.autoClockOut),
      to: "/hr/settings#crew",
    },
    {
      title: "Live GPS",
      what: "Crew on the clock show on the Home map",
      status: onOff(s.field.liveGps),
      to: "/hr/settings#crew",
    },
    {
      title: "Office alerts",
      what: "New requests, approved quotes, payments",
      status: [alertsOn ? "success" : "inactive", `${String(alertsOn)} of 3 on`],
      to: "/settings/alerts",
    },
    {
      title: "Eunice",
      what: "The text receptionist",
      status:
        s.receptionist.mode === "live"
          ? ["success", "Live"]
          : s.receptionist.mode === "practice"
            ? ["informative", "Practice"]
            : ["inactive", "Off"],
      to: "/receptionist",
    },
    {
      title: "Google review link",
      what: "Review requests need it",
      status: s.marketing.reviewUrl ? ["success", "Set"] : ["warning", "Not set"],
      to: "/marketing/settings#reviews",
    },
    {
      title: "Posting to Google and Facebook",
      what: "Approved posts and replies go out",
      status: onOff(s.marketing.postingLive),
      to: "/marketing/settings#posting",
    },
    {
      title: "Website requests",
      what: "Sites allowed to send requests",
      status: s.website.origins.length ? ["success", `${String(s.website.origins.length)} connected`] : ["inactive", "None"],
      to: "/requests/settings#websites",
    },
  ];
  return rows.filter((r): r is Glance => r !== false);
}

export function SettingsOverview() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const readsSettings = can("settings.read");
  const autos = useAutomations();
  const pages = usePages();
  const router = useRouter();
  if (!readsSettings)
    return (
      <div className="settings-groups">
        {GROUPS.map((g) => (
          <GroupCard key={g} group={g} pages={pages.filter((p) => p.group === g && !p.outside)} />
        ))}
      </div>
    );
  if (!s.data) return <Card title="Overview">Loading</Card>;
  const list = autos.data?.automations ?? null;
  // Only switches whose page this company has: head office skips the ones for work it doesn't do.
  const rows = glances(
    s.data.settings,
    company.trade,
    list ? list.filter((a) => a.on).length : null,
    list ? list.filter((a) => a.ready).length : 0,
  ).filter((r) => pages.some((p) => p.to === r.to.split("#")[0]));
  return (
    <>
      {!s.data.settings.messaging.live ? (
        <Banner
          type="warning"
          action={
            <Link to="/settings/messages" hash="sending" className="btn tertiary small">
              See sending
            </Link>
          }
        >
          Test mode is on. Texts, emails and card charges are saved but nothing goes to clients.
        </Banner>
      ) : null}
      <Card id="glance" title="Switches at a glance" flush>
        <ul className="glance">
          {rows.map((r) => (
            <li key={r.title}>
              <button type="button" onClick={() => router.history.push(r.to)}>
                <span className="what">
                  <b>{r.title}</b>
                  <span className="muted small">{r.what}</span>
                </span>
                <StatusLabel status={r.status[0]}>{r.status[1]}</StatusLabel>
                <Icon name="chevronRight" size={16} label="Change" />
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <div className="settings-groups">
        {GROUPS.map((g) => (
          <GroupCard key={g} group={g} pages={pages.filter((p) => p.group === g && !p.outside)} />
        ))}
      </div>
      <p className="muted small">Steward {STEWARD_VERSION}</p>
    </>
  );
}

function GroupCard({ group, pages }: { group: Group; pages: SettingsPage[] }) {
  if (!pages.length) return null;
  return (
    <Card title={group} flush>
      <ul className="settings-pages">
        {pages.map((p) => (
          <li key={p.to}>
            <Link to={p.to as "/settings"}>
              <Icon name={p.icon} size={18} />
              <span>
                <b>{p.label}</b>
                <span className="muted small">{p.blurb}</span>
              </span>
              <Icon name="chevronRight" size={16} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}
